import { Button } from '@app/components/various/Button';
import { Checkout, DoubleCheck, Plus, Remove } from '@app/components/various/Icons';
import { getSoldOutLabel } from '@app/ducks';
import { getProductSearchQuery } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { useToggleInOrder } from '@app/utils/hooks/useToggleInOrder';
import { Product } from '@typings/@centra';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useProductItemContext } from '../context/ProductItemContext';

interface Props {
  product: Product.Standard;
  className?: string;
  isCompact?: boolean;
}

export const ProductButtons = ({ product, className, isCompact = false }: Props) => {
  const { t } = useTranslation(['common', 'products', 'selections']);
  const { isVariantAvailable, onAddToSelection } = useProductItemContext();
  const { isLoading, isRemoving, isAdding, isInOrder, toggleInOrder, hasUnits } = useToggleInOrder({ product });

  const navigate = useNavigate();
  const soldOutLabel = useSelector(getSoldOutLabel);
  const handleTrashClick = () => {
    toggleInOrder();
  };

  const handleClick = () => {
    if (!isInOrder) {
      onAddToSelection?.();
      toggleInOrder();

      return;
    }

    navigate({ search: getProductSearchQuery({ productId: product.product }) });
  };

  const buttonCopy = React.useMemo(() => {
    if (!isVariantAvailable) {
      return soldOutLabel;
    }

    if (isAdding) {
      return t('common:adding');
    }

    if (isRemoving) {
      return t('common:removing');
    }

    if (!isInOrder) {
      return isCompact ? t('common:add') : t('selections:add_to_selection');
    }

    if (hasUnits) {
      return isCompact ? t('common:added') : t('selections:added_to_selection');
    }

    return isCompact ? t('products:add_quantity_short') : t('products:add_quantities');
  }, [isAdding, isInOrder, hasUnits, isCompact, soldOutLabel, isRemoving, isVariantAvailable, t]);

  const shouldShowCompactButton = isInOrder && !isLoading;
  const buttonIcon = React.useMemo(() => {
    if (!isVariantAvailable || isAdding || isRemoving) {
      return undefined;
    }

    if (!isInOrder) {
      return Checkout;
    }

    if (hasUnits) {
      return DoubleCheck;
    }

    return Plus;
  }, [isAdding, isInOrder, hasUnits, isVariantAvailable, isRemoving]);
  const buttonSize = isCompact ? 'medium' : 'large';

  return (
    <div className={cn('flex gap-1', className)}>
      <Button
        isFluid
        {...(hasUnits && {
          color: 'secondary',
          variant: 'bordered',
        })}
        isLoading={isLoading}
        size={buttonSize}
        disabled={!isVariantAvailable}
        icon={buttonIcon}
        data-testid="addProductButton"
        onClick={handleClick}
      >
        {buttonCopy}
      </Button>
      {shouldShowCompactButton && (
        <Button
          size={buttonSize}
          icon={Remove}
          color="secondary"
          variant="bordered"
          aria-label={t('selections:remove_from_selection')}
          data-testid="removeProductButton"
          onClick={handleTrashClick}
        />
      )}
    </div>
  );
};
